import React, { useEffect } from 'react';
import { Fade } from '../../components/Fade';
import { Button } from '../../components/Button';
import { FeaturesList } from '../../components/FeaturesList';
import { UpsellProgressBar } from '../../components/UpsellProgressBar';
import { Loader } from '../../components/Loader';
import { NotFound } from '../../components/NotFound';
import { useStatusPurchase } from '../../shared/use-status-purchase';
import { useDocumentTitle } from '../../shared/use-document-title';
import { useRouting } from '../../shared/use-routing';
import { useUser } from '../../shared/use-user';
import { useAuthentication } from '../../shared/use-authentication';
import { getSubscriptionStatus } from '../../../lib/use-cases/get-subscription-status';
import { logEvent } from '../../../lib/shared/event-logger';
import { pagePathnames } from '../../pages/page-routes';
import { ACTIVE, CANCELED } from '../../../lib/shared/subscription-statuses';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { UPSELL_STEPS } from '../../../lib/shared/upsell-steps';
import premiumImageSrc from '../../assets/images/premium.png';
import classnames from 'classnames/bind';
import styles from './SubscriptionThankYouPage.css';

const cx = classnames.bind(styles);

function SubscriptionThankYouPage() {
    const { userData } = useUser();
    const { signInBySubscription } = useAuthentication();
    const { params: { subscriptionId }, parseLocationSearch, buildPath } = useRouting();
    const isUpsellVisible = parseLocationSearch().is_upsell === 'true';

    const { purchaseData: data, isLoading } = useStatusPurchase({
        id: subscriptionId,
        getStatus: getSubscriptionStatus,
        signIn: signInBySubscription
    });

    useEffect(() => {
        if (data.status === ACTIVE) {
            logEvent(EVENT_TYPES.SUCCESS_STATUS_VIEWED);
        }
    }, [data.status]);

    useDocumentTitle('Subscription Status');

    if (!subscriptionId || (!isLoading && !data.plan)) {
        return <NotFound />;
    }

    const renderUpsellStatus = () => null;

    if (data.status === ACTIVE || data.status === CANCELED) {
        return (
            <Fade triggerOnce>
                <main className={styles.main}>
                    {isUpsellVisible && <UpsellProgressBar currentStep={UPSELL_STEPS.ORDER_CONFIRMATION} />}
                    <div className={cx('content', { contentWithOffset: !isUpsellVisible })}>
                        <h1 className={styles.title}>
                            Thank You for Your Purchase!
                        </h1>
                        <p className={styles.description}>
                            Here’s everything included in your order:
                        </p>
                        <ul className={styles.products}>
                            <li className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <h2 className={styles.cardTitle}>{data.name}</h2>
                                    <img
                                        src={`${premiumImageSrc}?v=2`}
                                        alt='premium'
                                        className={styles.cardImage}
                                    />
                                </div>
                                <div className={styles.priceBlock}>
                                    <span>{data.trial ? data.trial.price : data.price}</span>
                                    <span>then {data.price} every {data.billingPeriod.value} {data.billingPeriod.value === 1 ? data.billingPeriod.unit : `${data.billingPeriod.unit}s`}</span>
                                </div>
                                <div className={styles.cardContent}>
                                    <FeaturesList />
                                </div>
                            </li>
                            {data.upsells?.map(renderUpsellStatus)}
                        </ul>
                        <Button
                            className={styles.button}
                            link={{
                                to: buildPath(userData.isAuthenticated ? pagePathnames.dashboard : pagePathnames.signIn)
                            }}
                            label={userData.isAuthenticated ? 'Send invite' : 'Go to my account'}
                        />
                    </div>
                </main>
            </Fade>
        );
    }

    return <div className={styles.loaderWrapper}><Loader variant='secondary' /></div>;
}

export {
    SubscriptionThankYouPage
};
